import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, Typography, Paper, Grid, useTheme, Select, MenuItem,
    FormControl, InputLabel, Button, CircularProgress, Alert, Tooltip,
    Collapse, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, Stack, Chip
} from '@mui/material';
import {
    FaChartPie, FaFilter, FaSync, FaChevronDown, FaChevronUp, FaUserMd, FaHospitalAlt, FaBriefcaseMedical, FaTags
} from 'react-icons/fa';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import api, { getCompetences, API_URL } from '../api/api';
import '../styles/ux-enhancements.css';

const formatCurrency = (value) => {
    if (value === null || value === undefined) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
};

// --- Componente Linha de Procedimento (Traceability level) ---
const SubRow = ({ item }) => {
    const [open, setOpen] = useState(false);
    const hasTraceability = Array.isArray(item.detalhe_despesas) && item.detalhe_despesas.length > 0;

    return (
        <React.Fragment>
            <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
                <TableCell>
                    {hasTraceability && (
                        <IconButton size="tiny" onClick={() => setOpen(!open)} sx={{ p: 0.5 }}>
                            {open ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
                        </IconButton>
                    )}
                </TableCell>
                <TableCell>
                    <b>{item.codigo}</b> - {item.descricao}
                    {item.peso_uc > 1 && <Chip label={`Peso: ${item.peso_uc}`} size="small" sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} />}
                </TableCell>
                <TableCell>{item.centro_custo}</TableCell>
                <TableCell align="right">{formatCurrency(item.faturamento)}</TableCell>
                <TableCell align="right">{formatCurrency(item.custo_direto)}</TableCell>
                <TableCell align="right">{formatCurrency(item.custo_indireto)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(item.custo_total)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', color: item.margem >= 0 ? 'success.main' : 'error.main' }}>
                    {formatCurrency(item.margem)}
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 1, p: 2, bgcolor: 'action.hover', borderRadius: 1, border: '1px dashed', borderColor: 'divider' }}>
                            <Typography variant="caption" fontWeight="bold" gutterBottom component="div" sx={{ mb: 1, color: 'text.secondary' }}>
                                RASTREABILIDADE DE CUSTOS (REGRAS DE RATEIO E ORIGENS)
                            </Typography>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell width={30} />
                                        <TableCell><Typography variant="caption" fontWeight="bold">Descrição da Alocação / Conta</Typography></TableCell>
                                        <TableCell align="right"><Typography variant="caption" fontWeight="bold">Vl. Total no CC</Typography></TableCell>
                                        <TableCell align="right"><Typography variant="caption" fontWeight="bold">Custo para este Item (Unit)</Typography></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {item.detalhe_despesas.map((d, idx) => (
                                        <TableRow key={idx}>
                                            <TableCell sx={{ fontSize: '0.75rem' }}>{d.descricao}</TableCell>
                                            <TableCell align="right" sx={{ fontSize: '0.75rem' }}>{formatCurrency(d.valor_total_cc)}</TableCell>
                                            <TableCell align="right" sx={{ fontSize: '0.75rem' }}>{formatCurrency(d.custo_unitario_despesa)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </React.Fragment>
    );
};

// --- Componente Linha Expansível (Drill-Down) ---
const Row = ({ atendimento, items }) => {
    const [open, setOpen] = useState(false);

    // Sum aggregates for the row
    const totalFaturamento = items.reduce((sum, i) => sum + (i.faturamento || 0), 0);
    const totalDireto = items.reduce((sum, i) => sum + i.custo_direto, 0);
    const totalIndireto = items.reduce((sum, i) => sum + i.custo_indireto, 0);
    const totalFinal = items.reduce((sum, i) => sum + i.custo_total, 0);
    const totalMargem = totalFaturamento - totalFinal;
    const especialidade = items[0]?.especialidade;
    const convenio = items[0]?.convenio;
    const tipo = items[0]?.tipo_atendimento;

    return (
        <React.Fragment>
            <TableRow sx={{ '& > *': { borderBottom: 'unset' }, backgroundColor: open ? 'action.hover' : 'inherit' }}>
                <TableCell>
                    <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
                        {open ? <FaChevronUp /> : <FaChevronDown />}
                    </IconButton>
                </TableCell>
                <TableCell component="th" scope="row">
                    <Typography variant="body2" fontWeight="bold">{atendimento}</Typography>
                    <Typography variant="caption" color="text.secondary">{tipo} | {convenio}</Typography>
                </TableCell>
                <TableCell>{especialidade}</TableCell>
                <TableCell align="right">{formatCurrency(totalFaturamento)}</TableCell>
                <TableCell align="right">{formatCurrency(totalDireto)}</TableCell>
                <TableCell align="right">{formatCurrency(totalIndireto)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(totalFinal)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', color: totalMargem >= 0 ? 'success.main' : 'error.main' }}>
                    {formatCurrency(totalMargem)}
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 1, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="subtitle2" gutterBottom component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <FaTags /> Detalhamento de Procedimentos e Insumos
                            </Typography>
                            <Table size="small" aria-label="purchases">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: 'action.selected' }}>
                                        <TableCell width={30} />
                                        <TableCell>Código / Descrição</TableCell>
                                        <TableCell>Centro Resultante</TableCell>
                                        <TableCell align="right">Faturamento</TableCell>
                                        <TableCell align="right">Custo Direto</TableCell>
                                        <TableCell align="right">Rateio Alocado</TableCell>
                                        <TableCell align="right">Custo Total</TableCell>
                                        <TableCell align="right">Margem</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {items.map((item) => (
                                        <SubRow key={item.id} item={item} />
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </React.Fragment>
    );
};


const AdvancedCostAnalysisPage = () => {
    const theme = useTheme();

    // --- State ---
    const [meses, setMeses] = useState([]);
    const [selectedMes, setSelectedMes] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [rawData, setRawData] = useState([]);
    const [contaOptions, setContaOptions] = useState([]);

    // --- Filtros ---
    const [filterEspecialidade, setFilterEspecialidade] = useState('Todos');
    const [filterConvenio, setFilterConvenio] = useState('Todos');
    const [filterTipoAtendimento, setFilterTipoAtendimento] = useState('Todos');
    const [filterCentroCusto, setFilterCentroCusto] = useState('Todos');
    const [filterMedico, setFilterMedico] = useState('Todos');
    const [filterCarater, setFilterCarater] = useState('Todos');
    const [filterProcedimento, setFilterProcedimento] = useState('Todos');
    const [filterConta, setFilterConta] = useState('Todos');

    // --- Paginação do Drill Down ---
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // --- Load Competences ---
    useEffect(() => {
        const fetchCompetences = async () => {
            try {
                const res = await getCompetences();
                if (res.data && Array.isArray(res.data)) {
                    const months = res.data.map(c => c.mes || c).sort().reverse();
                    setMeses(months);
                    setSelectedMes(months[0]);
                } else if (res.data && res.data.length > 0) {
                    // Fallback to empty if it's not an array
                    setMeses([]);
                }
            } catch (err) {
                console.error("Erro ao buscar competências:", err);
            }
        };
        const fetchContas = async () => {
            try {
                const res = await api.get(`${API_URL}/config/plano_contas_data`);
                if (res.data) setContaOptions(res.data);
            } catch (err) {
                console.error("Erro ao carregar contas:", err);
            }
        };
        fetchCompetences();
        fetchContas();
    }, []);

    // --- Load Data ---
    const loadData = async () => {
        if (!selectedMes) return;
        setLoading(true);
        setError(null);
        try {
            const mesStr = selectedMes.mes || selectedMes;
            const res = await api.get(`${API_URL}/advanced-analysis/costs/${mesStr}`);
            const dataArr = res.data.data;
            setRawData(Array.isArray(dataArr) ? dataArr : []);
            // Reseta filtros
            setFilterEspecialidade('Todos');
            setFilterConvenio('Todos');
            setFilterTipoAtendimento('Todos');
            setFilterCentroCusto('Todos');
            setFilterMedico('Todos');
            setFilterCarater('Todos');
            setFilterProcedimento('Todos');
            setFilterConta('Todos');
            setPage(0);
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.error || "Erro ao carregar os dados de Análise Avançada");
            setRawData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, [selectedMes]);

    // --- Derived Filters Options ---
    const especialidadesDisponiveis = useMemo(() => {
        if (!Array.isArray(rawData)) return ['Todos'];
        return ['Todos', ...new Set(rawData.map(d => d.especialidade).filter(Boolean))].sort();
    }, [rawData]);

    const conveniosDisponiveis = useMemo(() => {
        if (!Array.isArray(rawData)) return ['Todos'];
        return ['Todos', ...new Set(rawData.map(d => d.convenio).filter(Boolean))].sort();
    }, [rawData]);

    const tiposDisponiveis = useMemo(() => {
        if (!Array.isArray(rawData)) return ['Todos'];
        return ['Todos', ...new Set(rawData.map(d => d.tipo_atendimento).filter(Boolean))].sort();
    }, [rawData]);

    const centrosCustoDisponiveis = useMemo(() => {
        if (!Array.isArray(rawData)) return ['Todos'];
        return ['Todos', ...new Set(rawData.map(d => d.centro_custo).filter(Boolean))].sort();
    }, [rawData]);

    const medicosDisponiveis = useMemo(() => {
        if (!Array.isArray(rawData)) return ['Todos'];
        return ['Todos', ...new Set(rawData.map(d => d.medico_atendimento).filter(Boolean))].sort();
    }, [rawData]);

    const caraterDisponivel = useMemo(() => {
        if (!Array.isArray(rawData)) return ['Todos'];
        return ['Todos', ...new Set(rawData.map(d => d.carater_atendimento).filter(Boolean))].sort();
    }, [rawData]);

    const procedimentosDisponiveis = useMemo(() => {
        if (!Array.isArray(rawData)) return ['Todos'];
        return ['Todos', ...new Set(rawData.map(d => d.codigo ? `${d.codigo} - ${d.descricao}` : false).filter(Boolean))].sort();
    }, [rawData]);

    const contasDisponiveis = useMemo(() => {
        if (!Array.isArray(rawData)) return [{ value: 'Todos', label: 'Todos' }];
        const codes = [...new Set(rawData.map(d => d.conta).filter(Boolean))].sort();
        return [{ value: 'Todos', label: 'Todos' }, ...codes.map(c => {
            const acc = contaOptions.find(opt => opt.Conta === c);
            return { value: c, label: acc ? `${acc.Conta} - ${acc.Descricao}` : c };
        })];
    }, [rawData, contaOptions]);

    // --- Apply Filters ---
    const filteredData = useMemo(() => {
        return rawData.filter(d => {
            if (filterEspecialidade !== 'Todos' && d.especialidade !== filterEspecialidade) return false;
            if (filterConvenio !== 'Todos' && d.convenio !== filterConvenio) return false;
            if (filterTipoAtendimento !== 'Todos' && d.tipo_atendimento !== filterTipoAtendimento) return false;
            if (filterCentroCusto !== 'Todos' && d.centro_custo !== filterCentroCusto) return false;
            if (filterMedico !== 'Todos' && d.medico_atendimento !== filterMedico) return false;
            if (filterCarater !== 'Todos' && d.carater_atendimento !== filterCarater) return false;
            if (filterConta !== 'Todos' && d.conta !== filterConta) return false;
            if (filterProcedimento !== 'Todos') {
                const procStr = d.codigo ? `${d.codigo} - ${d.descricao}` : '';
                if (procStr !== filterProcedimento) return false;
            }
            return true;
        });
    }, [rawData, filterEspecialidade, filterConvenio, filterTipoAtendimento, filterCentroCusto, filterMedico, filterCarater, filterProcedimento, filterConta]);

    // --- Grouping Data for Drill Down ---
    const groupedByAtendimento = useMemo(() => {
        const groups = {};
        filteredData.forEach(d => {
            if (!groups[d.atendimento]) groups[d.atendimento] = [];
            groups[d.atendimento].push(d);
        });
        // Convert to array and sort by total cost desc
        return Object.entries(groups)
            .map(([atendimento, items]) => ({
                atendimento,
                items,
                total: items.reduce((sum, i) => sum + i.custo_total, 0)
            }))
            .sort((a, b) => b.total - a.total);
    }, [filteredData]);

    const paginatedGroupedData = groupedByAtendimento.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    // --- Chart Data Processing ---

    // 1. Receita vs Custo por Especialidade (Top 5)
    const costBySpecialty = useMemo(() => {
        const map = {};
        filteredData.forEach(d => {
            const esp = d.especialidade || 'Outros';
            if (!map[esp]) map[esp] = { faturamento: 0, total: 0 };
            map[esp].faturamento += (d.faturamento || 0);
            map[esp].total += d.custo_total;
        });
        const sorted = Object.entries(map).sort((a, b) => b[1].faturamento - a[1].faturamento).slice(0, 5);
        return {
            labels: sorted.map(i => i[0]),
            datasets: [
                {
                    label: 'Faturamento (R$)',
                    data: sorted.map(i => i[1].faturamento),
                    backgroundColor: 'rgba(75, 192, 192, 0.7)',
                },
                {
                    label: 'Custo Total (R$)',
                    data: sorted.map(i => i[1].total),
                    backgroundColor: 'rgba(255, 99, 132, 0.7)',
                }
            ]
        };
    }, [filteredData]);

    // 2. Receita vs Custo por Convênio (Top 5)
    const costByInsurance = useMemo(() => {
        const map = {};
        filteredData.forEach(d => {
            const conv = d.convenio || 'Outros';
            if (!map[conv]) map[conv] = { faturamento: 0, total: 0 };
            map[conv].faturamento += (d.faturamento || 0);
            map[conv].total += d.custo_total;
        });
        const sorted = Object.entries(map).sort((a, b) => b[1].faturamento - a[1].faturamento).slice(0, 5);
        return {
            labels: sorted.map(i => i[0]),
            datasets: [
                {
                    label: 'Faturamento (R$)',
                    data: sorted.map(i => i[1].faturamento),
                    backgroundColor: 'rgba(54, 162, 235, 0.7)',
                },
                {
                    label: 'Custo Total (R$)',
                    data: sorted.map(i => i[1].total),
                    backgroundColor: 'rgba(255, 159, 64, 0.7)',
                }
            ]
        };
    }, [filteredData]);

    // 3. Receita vs Custo Médio por Tipo
    const costAvgByType = useMemo(() => {
        const map = {};
        filteredData.forEach(d => {
            const tipo = d.tipo_atendimento || 'Outros';
            if (!map[tipo]) map[tipo] = { total_f: 0, total_c: 0, atendimentos: new Set() };
            map[tipo].total_f += (d.faturamento || 0);
            map[tipo].total_c += d.custo_total;
            map[tipo].atendimentos.add(d.atendimento);
        });
        const result = Object.entries(map).map(([t, val]) => ({
            tipo: t,
            media_f: val.total_f / (val.atendimentos.size || 1),
            media_c: val.total_c / (val.atendimentos.size || 1)
        })).sort((a, b) => b.media_f - a.media_f);

        return {
            labels: result.map(i => i.tipo),
            datasets: [
                {
                    label: 'Rec. Média (R$)',
                    data: result.map(i => i.media_f),
                    backgroundColor: 'rgba(75, 192, 192, 0.7)',
                },
                {
                    label: 'Custo Médio (R$)',
                    data: result.map(i => i.media_c),
                    backgroundColor: 'rgba(153, 102, 255, 0.7)',
                }
            ]
        };
    }, [filteredData]);

    // 4. Custo por Centro de Custo (Top 5)
    const costByCostCenter = useMemo(() => {
        const map = {};
        filteredData.forEach(d => {
            const cc = d.centro_custo || 'Outros';
            map[cc] = (map[cc] || 0) + d.custo_total;
        });
        const sorted = Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 5);
        return {
            labels: sorted.map(i => i[0]),
            datasets: [{
                label: 'Custo Total (R$)',
                data: sorted.map(i => i[1]),
                backgroundColor: 'rgba(153, 102, 255, 0.7)',
            }]
        };
    }, [filteredData]);

    // 5. Top Médicos (Top 10)
    const costByDoctor = useMemo(() => {
        const map = {};
        filteredData.forEach(d => {
            const doc = d.medico_atendimento || 'Outros';
            map[doc] = (map[doc] || 0) + d.custo_total;
        });
        const sorted = Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 10);
        return {
            labels: sorted.map(i => i[0]),
            datasets: [{
                label: 'Custo Gerado (R$)',
                data: sorted.map(i => i[1]),
                backgroundColor: 'rgba(255, 159, 64, 0.7)',
            }]
        };
    }, [filteredData]);

    // 6. Custo por Caráter de Atendimento
    const costByCharacter = useMemo(() => {
        const map = {};
        filteredData.forEach(d => {
            const car = d.carater_atendimento || 'Outros';
            map[car] = (map[car] || 0) + d.custo_total;
        });
        const sorted = Object.entries(map).sort((a, b) => b[1] - a[1]);
        return {
            labels: sorted.map(i => i[0]),
            datasets: [{
                data: sorted.map(i => i[1]),
                backgroundColor: [
                    'rgba(54, 162, 235, 0.7)',
                    'rgba(255, 99, 132, 0.7)',
                    'rgba(255, 206, 86, 0.7)',
                    'rgba(75, 192, 192, 0.7)',
                ],
            }]
        };
    }, [filteredData]);

    // --- KPIs Totais ---
    const kpiTotalCustos = Array.isArray(filteredData) ? filteredData.reduce((acc, curr) => acc + curr.custo_total, 0) : 0;
    const kpiTotalFaturamento = Array.isArray(filteredData) ? filteredData.reduce((acc, curr) => acc + (curr.faturamento || 0), 0) : 0;
    const kpiTotalMargem = kpiTotalFaturamento - kpiTotalCustos;
    const kpiPercentualMargem = kpiTotalFaturamento > 0 ? (kpiTotalMargem / kpiTotalFaturamento) * 100 : 0;

    const kpiTotalAtendimentos = Array.isArray(filteredData) ? new Set(filteredData.map(d => d.atendimento)).size : 0;
    const kpiCustoMedio = kpiTotalAtendimentos > 0 ? kpiTotalCustos / kpiTotalAtendimentos : 0;
    const kpiFaturamentoMedio = kpiTotalAtendimentos > 0 ? kpiTotalFaturamento / kpiTotalAtendimentos : 0;

    const kpiTotalProcedimentos = Array.isArray(filteredData) ? filteredData.reduce((acc, curr) => acc + (curr.quantidade || 0), 0) : 0;
    const custoIndiretoTotal = Array.isArray(filteredData) ? filteredData.reduce((acc, curr) => acc + (curr.custo_indireto || 0), 0) : 0;
    const kpiPercentualIndireto = kpiTotalCustos > 0 ? (custoIndiretoTotal / kpiTotalCustos) * 100 : 0;

    return (
        <Box sx={{ p: 4, backgroundColor: 'background.default', minHeight: '100vh' }} className="page-fade-in">
            {/* Cabecalho */}
            <Typography variant="h3" fontWeight="bold" gutterBottom className="premium-gradient-text" sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                <FaChartPie /> Análise de Custos Avançada
            </Typography>

            {/* Header de Ações */}
            <Paper elevation={3} sx={{ p: 3, mb: 4, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Mês de Processamento</InputLabel>
                            <Select
                                value={selectedMes.mes || selectedMes || ''}
                                onChange={(e) => setSelectedMes(e.target.value)}
                                label="Mês de Processamento"
                            >
                                {Array.isArray(meses) && meses.map(m => (
                                    <MenuItem key={m.mes || m} value={m.mes || m}>{m.mes || m}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<FaSync />}
                            onClick={loadData}
                            disabled={loading || !selectedMes}
                            fullWidth
                            sx={{ borderRadius: 2 }}
                        >
                            Atualizar
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" height="40vh">
                    <CircularProgress size={60} />
                </Box>
            ) : error ? (
                <Alert severity="error" sx={{ mb: 4, borderRadius: 2 }}>{error}</Alert>
            ) : rawData.length === 0 && selectedMes ? (
                <Alert severity="info" sx={{ mb: 4, borderRadius: 2 }}>Nenhum dado encontrado para a competência {selectedMes.mes || selectedMes}.</Alert>
            ) : rawData.length > 0 ? (
                <>
                    {/* Filtros em Linha */}
                    <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Chip icon={<FaFilter />} label="Filtros Dinâmicos" color="primary" variant="outlined" />
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel>Especialidade</InputLabel>
                            <Select value={filterEspecialidade} onChange={(e) => setFilterEspecialidade(e.target.value)} label="Especialidade">
                                {especialidadesDisponiveis.map(e => <MenuItem key={e} value={e}>{e}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel>Convênio</InputLabel>
                            <Select value={filterConvenio} onChange={(e) => setFilterConvenio(e.target.value)} label="Convênio">
                                {conveniosDisponiveis.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel>Tipo de Atendimento</InputLabel>
                            <Select value={filterTipoAtendimento} onChange={(e) => setFilterTipoAtendimento(e.target.value)} label="Tipo de Atendimento">
                                {tiposDisponiveis.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel>Centro de Custo</InputLabel>
                            <Select value={filterCentroCusto} onChange={(e) => setFilterCentroCusto(e.target.value)} label="Centro de Custo">
                                {centrosCustoDisponiveis.map(cc => <MenuItem key={cc} value={cc}>{cc}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel>Médico</InputLabel>
                            <Select value={filterMedico} onChange={(e) => setFilterMedico(e.target.value)} label="Médico">
                                {medicosDisponiveis.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel>Caráter</InputLabel>
                            <Select value={filterCarater} onChange={(e) => setFilterCarater(e.target.value)} label="Caráter">
                                {caraterDisponivel.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel>Procedimento / Item</InputLabel>
                            <Select value={filterProcedimento} onChange={(e) => setFilterProcedimento(e.target.value)} label="Procedimento / Item">
                                {procedimentosDisponiveis.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel>Conta Contábil</InputLabel>
                            <Select value={filterConta} onChange={(e) => setFilterConta(e.target.value)} label="Conta Contábil">
                                {contasDisponiveis.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Box>

                    {/* KPIs */}
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        <Grid item xs={12} md={3}>
                            <Paper sx={{ p: 4, borderRadius: 3, textAlign: 'center', background: 'linear-gradient(135deg, rgba(75,192,192,0.1) 0%, rgba(75,192,192,0) 100%)', border: '1px solid rgba(75,192,192,0.2)' }}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}><FaTags /> Faturamento Total</Typography>
                                <Typography variant="h4" fontWeight="bold" sx={{ color: '#00897b' }}>{formatCurrency(kpiTotalFaturamento)}</Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Paper sx={{ p: 4, borderRadius: 3, textAlign: 'center', background: 'linear-gradient(135deg, rgba(211,47,47,0.1) 0%, rgba(211,47,47,0) 100%)', border: '1px solid rgba(211,47,47,0.2)' }}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}><FaHospitalAlt /> Custo Total</Typography>
                                <Typography variant="h4" fontWeight="bold" sx={{ color: '#d32f2f' }}>{formatCurrency(kpiTotalCustos)}</Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Paper sx={{ p: 4, borderRadius: 3, textAlign: 'center', background: 'linear-gradient(135deg, rgba(156,39,176,0.1) 0%, rgba(156,39,176,0) 100%)', border: '1px solid rgba(156,39,176,0.2)' }}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}><FaChartPie /> Margem de Contribuição</Typography>
                                <Typography variant="h4" fontWeight="bold" sx={{ color: kpiTotalMargem >= 0 ? '#7b1fa2' : '#c62828' }}>{formatCurrency(kpiTotalMargem)}</Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Paper sx={{ p: 4, borderRadius: 3, textAlign: 'center', background: 'linear-gradient(135deg, rgba(25,118,210,0.1) 0%, rgba(25,118,210,0) 100%)', border: '1px solid rgba(25,118,210,0.2)' }}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}><FaSync /> % Margem Geral</Typography>
                                <Typography variant="h4" fontWeight="bold" sx={{ color: kpiPercentualMargem >= 0 ? '#1976d2' : '#c62828' }}>{kpiPercentualMargem.toFixed(1)}%</Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Paper sx={{ p: 4, borderRadius: 3, textAlign: 'center', background: 'linear-gradient(135deg, rgba(255,159,64,0.1) 0%, rgba(255,159,64,0) 100%)', border: '1px solid rgba(255,159,64,0.2)' }}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}><FaUserMd /> Atendimentos</Typography>
                                <Typography variant="h4" fontWeight="bold" sx={{ color: '#f57c00' }}>{kpiTotalAtendimentos}</Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Paper sx={{ p: 4, borderRadius: 3, textAlign: 'center', background: 'linear-gradient(135deg, rgba(56,142,60,0.1) 0%, rgba(56,142,60,0) 100%)', border: '1px solid rgba(56,142,60,0.2)' }}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}><FaHospitalAlt /> Ticket Médio (Receita)</Typography>
                                <Typography variant="h4" fontWeight="bold" sx={{ color: '#388e3c' }}>{formatCurrency(kpiFaturamentoMedio)}</Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Paper sx={{ p: 4, borderRadius: 3, textAlign: 'center', background: 'linear-gradient(135deg, rgba(3,169,244,0.1) 0%, rgba(3,169,244,0) 100%)', border: '1px solid rgba(3,169,244,0.2)' }}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}><FaChartPie /> % Rateio Indireto</Typography>
                                <Typography variant="h4" fontWeight="bold" sx={{ color: '#03a9f4' }}>{kpiPercentualIndireto.toFixed(1)}%</Typography>
                            </Paper>
                        </Grid>
                    </Grid>

                    {/* Charts */}
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        <Grid item xs={12} lg={6}>
                            <Paper sx={{ p: 3, borderRadius: 3, height: 400, display: 'flex', flexDirection: 'column' }}>
                                <Typography variant="h6" fontWeight="bold" gutterBottom>Custos por Especialidade (Direto vs Indireto)</Typography>
                                <Box sx={{ flexGrow: 1, position: 'relative' }}>
                                    <Bar data={costBySpecialty} options={{ responsive: true, maintainAspectRatio: false, scales: { x: { stacked: true }, y: { stacked: true } } }} />
                                </Box>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} lg={3}>
                            <Paper sx={{ p: 3, borderRadius: 3, height: 400, display: 'flex', flexDirection: 'column' }}>
                                <Typography variant="h6" fontWeight="bold" gutterBottom>Custo por Convênio</Typography>
                                <Box sx={{ flexGrow: 1, position: 'relative' }}>
                                    <Doughnut data={costByInsurance} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } } }} />
                                </Box>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} lg={3}>
                            <Paper sx={{ p: 3, borderRadius: 3, height: 400, display: 'flex', flexDirection: 'column' }}>
                                <Typography variant="h6" fontWeight="bold" gutterBottom>Custo Médio por Tipo</Typography>
                                <Box sx={{ flexGrow: 1, position: 'relative' }}>
                                    <Bar data={costAvgByType} options={{ responsive: true, maintainAspectRatio: false, indexAxis: 'y' }} />
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>

                    {/* Charts Row 2 */}
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        <Grid item xs={12} lg={4}>
                            <Paper sx={{ p: 3, borderRadius: 3, height: 400, display: 'flex', flexDirection: 'column' }}>
                                <Typography variant="h6" fontWeight="bold" gutterBottom>Custos por Centro de Custo (Top 5)</Typography>
                                <Box sx={{ flexGrow: 1, position: 'relative' }}>
                                    <Bar data={costByCostCenter} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } }} />
                                </Box>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} lg={4}>
                            <Paper sx={{ p: 3, borderRadius: 3, height: 400, display: 'flex', flexDirection: 'column' }}>
                                <Typography variant="h6" fontWeight="bold" gutterBottom>Custos por Médico (Top 10)</Typography>
                                <Box sx={{ flexGrow: 1, position: 'relative' }}>
                                    <Bar data={costByDoctor} options={{ responsive: true, maintainAspectRatio: false, indexAxis: 'y', plugins: { legend: { display: false } } }} />
                                </Box>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} lg={4}>
                            <Paper sx={{ p: 3, borderRadius: 3, height: 400, display: 'flex', flexDirection: 'column' }}>
                                <Typography variant="h6" fontWeight="bold" gutterBottom>Caráter de Atendimento</Typography>
                                <Box sx={{ flexGrow: 1, position: 'relative' }}>
                                    <Doughnut data={costByCharacter} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } } }} />
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>

                    {/* Tabela de Drill-Down */}
                    <Paper sx={{ p: 0, borderRadius: 3, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                        <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'action.hover' }}>
                            <Typography variant="h6" fontWeight="bold">Drill-Down: Visão por Atendimento</Typography>
                            <Typography variant="body2" color="text.secondary">Investigue todos os procedimentos e insumos amarrados e rateados à conta do paciente.</Typography>
                        </Box>
                        <TableContainer sx={{ maxHeight: 600 }}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell width={50} />
                                        <TableCell><b>Nr. Atendimento (Conta)</b></TableCell>
                                        <TableCell><b>Especialidade Responsável</b></TableCell>
                                        <TableCell align="right"><b>Faturamento (R$)</b></TableCell>
                                        <TableCell align="right"><b>Custo Direto (R$)</b></TableCell>
                                        <TableCell align="right"><b>Rateios (R$)</b></TableCell>
                                        <TableCell align="right"><b>Custo Total (R$)</b></TableCell>
                                        <TableCell align="right"><b>Margem (R$)</b></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {Array.isArray(paginatedGroupedData) && paginatedGroupedData.map((row) => (
                                        <Row key={row.atendimento} atendimento={row.atendimento} items={row.items} />
                                    ))}
                                    {(!Array.isArray(paginatedGroupedData) || paginatedGroupedData.length === 0) && (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                                Nenhum atendimento corresponde aos filtros atuais.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            rowsPerPageOptions={[10, 25, 50, 100]}
                            component="div"
                            count={groupedByAtendimento.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={(e, newPage) => setPage(newPage)}
                            onRowsPerPageChange={(e) => {
                                setRowsPerPage(parseInt(e.target.value, 10));
                                setPage(0);
                            }}
                            labelRowsPerPage="Linhas por página:"
                            labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
                        />
                    </Paper>

                </>
            ) : (
                <Box display="flex" justifyContent="center" alignItems="center" height="40vh">
                    <Typography variant="h6" color="textSecondary" sx={{ fontWeight: 'medium' }}>
                        Selecione um mês de processamento ou aguarde o primeiro fechamento de competência.
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default AdvancedCostAnalysisPage;
